'use client';

import { logger } from '@adornis/base/logging.js';
import { AlertTriangle, Check, Send, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  proposeExamResultDistributionQuery,
  releaseExamResultsMutation,
  revokeExamResultsMutation,
  type DistributionCandidateDTO,
} from '../../../../../../db/api/distribute-exam-results.js';
import { EXAM_RESULT_DETAIL_LEVELS, type ExamResultDetailLevel } from '../../../../../../db/exam-result-release.js';
import { getStudentsByIdsQuery } from '../../../../../../db/api/get-students-by-ids.js';
import { Button } from '../../../../ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../ui/dialog.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/select.js';

/** Empty string is the "do not distribute this one" choice — an explicit decision, not a missing one. */
const EXCLUDED = '';
/** Radix `Select.Item` rejects an empty-string value outright, so the per-row assignment select needs its
 * own non-empty stand-in for `EXCLUDED` — translated back to `''` the moment it leaves the UI layer,
 * never stored in `assignments` and never sent to the mutation. */
const EXCLUDED_OPTION = '__exclude__';
const UNSET_OPTION = '__unset__';

interface DistributeResultsCardProps {
  examId: string | null | undefined;
}

/**
 * Hands an exam's results to the students they belong to.
 *
 * The teacher confirms every submission→student binding here, once. That is the whole safety story: the
 * server proposes bindings from the OCR'd names but will not act on them, and a name that matches nobody or
 * several people arrives unresolved for a human to settle. Nothing is released while any row is open.
 */
export function DistributeResultsCard({ examId }: DistributeResultsCardProps) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [lastReleasedAt, setLastReleasedAt] = useState<string | null>(null);
  const [lastDetailLevel, setLastDetailLevel] = useState<string | null>(null);
  // 'full' is the default — matches the feature's original behavior before levels existed, so an existing
  // habit of "open the dialog, assign, release" keeps doing what it always did unless the teacher changes it.
  const [detailLevel, setDetailLevel] = useState<ExamResultDetailLevel>('full');
  const [includeSubmissionFiles, setIncludeSubmissionFiles] = useState(false);
  const [candidates, setCandidates] = useState<DistributionCandidateDTO[]>([]);
  // Set the moment a release succeeds, cleared the next time the dialog is opened (see `loadProposal`) —
  // the dialog stays open and swaps its whole body for a confirmation instead of just closing silently,
  // since "it closed" is not itself evidence that anything actually happened.
  const [justReleased, setJustReleased] = useState(false);
  const [releasedCount, setReleasedCount] = useState(0);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [studentNames, setStudentNames] = useState<Record<string, string>>({});
  const [rosterIDs, setRosterIDs] = useState<string[]>([]);

  const loadProposal = async () => {
    if (!examId) return;
    setIsLoading(true);
    setJustReleased(false);
    try {
      const proposal = await proposeExamResultDistributionQuery(examId)({
        blockedReason: 1,
        lastReleasedAt: 1,
        lastDetailLevel: 1,
        lastIncludeSubmissionFiles: 1,
        unmatchedStudentIDs: 1,
        candidates: {
          submissionId: 1,
          studentName: 1,
          status: 1,
          proposedStudentID: 1,
          candidateStudentIDs: 1,
          boundStudentID: 1,
        },
      });

      const rows = proposal?.candidates ?? [];
      setBlockedReason(proposal?.blockedReason ?? null);
      setLastReleasedAt(proposal?.lastReleasedAt ?? null);
      setLastDetailLevel(proposal?.lastDetailLevel ?? null);
      // Re-opening after a release pre-selects the level that is actually live, rather than resetting to
      // 'full' and inviting a re-release to silently widen what was deliberately kept narrow.
      if (
        proposal?.lastDetailLevel &&
        EXAM_RESULT_DETAIL_LEVELS.includes(proposal.lastDetailLevel as ExamResultDetailLevel)
      ) {
        setDetailLevel(proposal.lastDetailLevel as ExamResultDetailLevel);
      }
      if (proposal?.lastReleasedAt) setIncludeSubmissionFiles(Boolean(proposal.lastIncludeSubmissionFiles));
      setCandidates(rows);

      // Pre-fill only the unambiguous proposals. An ambiguous or unmatched row stays empty on purpose, so
      // the release button cannot light up until a human has actually looked at it.
      const seeded: Record<string, string> = {};
      for (const row of rows) {
        if (row.status === 'matched' && row.proposedStudentID) seeded[row.submissionId] = row.proposedStudentID;
      }
      setAssignments(seeded);

      const everyID = Array.from(
        new Set([
          ...(proposal?.unmatchedStudentIDs ?? []),
          ...rows.flatMap(row => [...(row.candidateStudentIDs ?? []), row.proposedStudentID ?? '']),
        ]),
      ).filter(Boolean) as string[];
      setRosterIDs(everyID);

      if (everyID.length > 0) {
        const students = await getStudentsByIdsQuery(everyID)({ _id: 1, firstName: 1, lastName: 1 });
        const names: Record<string, string> = {};
        for (const student of students ?? []) {
          names[student._id] = `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || student._id;
        }
        setStudentNames(names);
      }

      setIsOpen(true);
    } catch (err) {
      // Surface the server's own message rather than a generic string: the reasons this can fail are all
      // actionable ones the teacher can do something about (exam not linked to a class, not your exam),
      // and hiding them behind "could not be loaded" makes a fixable state look like a broken feature.
      logger.error(err, 'Failed to load distribution proposal');
      const detail = err instanceof Error ? err.message : String(err ?? '');
      alert(
        detail ? `${t('Canvas.summary.distribute.loadError')}\n\n${detail}` : t('Canvas.summary.distribute.loadError'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Every submission must carry a decision — a student picked, or an explicit exclusion.
  const unresolvedCount = candidates.filter(row => assignments[row.submissionId] === undefined).length;
  const canRelease = !blockedReason && unresolvedCount === 0 && candidates.length > 0 && !isReleasing;

  const handleRelease = async () => {
    if (!examId) return;
    setIsReleasing(true);
    try {
      const submissionIds = candidates.map(row => row.submissionId);
      const studentIDs = submissionIds.map(id => assignments[id] ?? EXCLUDED);
      await releaseExamResultsMutation(examId, submissionIds, studentIDs, detailLevel, includeSubmissionFiles)();
      // Kept in sync locally rather than waiting for the next `loadProposal` round-trip, so the trigger
      // card's "already released" subtitle is correct immediately if the teacher reopens right away.
      setLastReleasedAt(new Date().toISOString());
      setLastDetailLevel(detailLevel);
      setReleasedCount(studentIDs.filter(Boolean).length);
      setJustReleased(true);
    } catch (err) {
      logger.error(err, 'Failed to release exam results');
      alert(err instanceof Error ? err.message : t('Canvas.summary.distribute.releaseError'));
    } finally {
      setIsReleasing(false);
    }
  };

  const handleRevoke = async () => {
    if (!examId) return;
    setIsReleasing(true);
    try {
      await revokeExamResultsMutation(examId)();
      setLastReleasedAt(null);
      setIsOpen(false);
    } catch (err) {
      logger.error(err, 'Failed to revoke exam results');
      alert(t('Canvas.summary.distribute.releaseError'));
    } finally {
      setIsReleasing(false);
    }
  };

  const statusBadge = (row: DistributionCandidateDTO) => {
    const assigned = assignments[row.submissionId];
    if (assigned) return null;
    if (assigned === EXCLUDED) return null;
    const tone =
      row.status === 'ambiguous'
        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
        : 'bg-muted text-muted-foreground';
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${tone}`}>
        {t(`Canvas.summary.distribute.status.${row.status}`)}
      </span>
    );
  };

  return (
    <>
      <div className="min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <h3 className="break-words text-sm font-semibold text-slate-700 dark:text-slate-300">
          {t('Canvas.summary.distribute.title')}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {lastReleasedAt
            ? t('Canvas.summary.distribute.alreadyReleased', {
                level: t(`Canvas.summary.distribute.level.${lastDetailLevel}.label`),
              })
            : t('Canvas.summary.distribute.description')}
        </p>
        <button
          disabled={!examId || isLoading}
          onClick={() => void loadProposal()}
          className="mt-4 inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-border bg-white dark:bg-secondary px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          <Send className="h-4 w-4 shrink-0" />
          <span className="min-w-0 break-words">{t('Canvas.summary.distribute.openButton')}</span>
        </button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-xl bg-card text-card-foreground border-border">
          {justReleased ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500">
                <Check className="h-6 w-6 text-white" strokeWidth={3} />
              </div>
              <div className="space-y-1">
                <DialogTitle>{t('Canvas.summary.distribute.successTitle')}</DialogTitle>
                <DialogDescription>
                  {t('Canvas.summary.distribute.successBody', { count: releasedCount })}
                </DialogDescription>
              </div>
              <Button onClick={() => setIsOpen(false)}>{t('Canvas.summary.distribute.successClose')}</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t('Canvas.summary.distribute.dialogTitle')}</DialogTitle>
                <DialogDescription>{t('Canvas.summary.distribute.dialogDescription')}</DialogDescription>
              </DialogHeader>

              {blockedReason && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{t(`Canvas.summary.distribute.blocked.${blockedReason}`)}</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {t('Canvas.summary.distribute.detailLevelLabel')}
                  </label>
                  <Select value={detailLevel} onValueChange={value => setDetailLevel(value as ExamResultDetailLevel)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXAM_RESULT_DETAIL_LEVELS.map(l => (
                        <SelectItem key={l} value={l}>
                          {t(`Canvas.summary.distribute.level.${l}.label`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t(`Canvas.summary.distribute.level.${detailLevel}.hint`)}
                  </p>
                </div>

                <button
                  type="button"
                  role="checkbox"
                  aria-checked={includeSubmissionFiles}
                  onClick={() => setIncludeSubmissionFiles(prev => !prev)}
                  className="flex items-center gap-2.5 text-sm text-foreground"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                      includeSubmissionFiles ? 'bg-brand-500 border-brand-500' : 'border-input bg-background'
                    }`}
                  >
                    {includeSubmissionFiles && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </span>
                  {t('Canvas.summary.distribute.includeFiles')}
                </button>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">{t('Canvas.summary.distribute.assignmentsLabel')}</p>
                <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
                  {candidates.map(row => {
                    const assigned = assignments[row.submissionId];
                    const selectValue =
                      assigned === undefined ? UNSET_OPTION : assigned === EXCLUDED ? EXCLUDED_OPTION : assigned;

                    return (
                      <div
                        key={row.submissionId}
                        className="flex items-center justify-between gap-3 rounded-lg bg-background px-3 py-2"
                      >
                        <div className="min-w-0 flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">
                            {row.studentName || t('Canvas.summary.distribute.noName')}
                          </p>
                          {statusBadge(row)}
                        </div>
                        <div className="w-44 shrink-0">
                          <Select
                            value={selectValue}
                            onValueChange={value => {
                              setAssignments(prev =>
                                value === UNSET_OPTION
                                  ? Object.fromEntries(Object.entries(prev).filter(([key]) => key !== row.submissionId))
                                  : { ...prev, [row.submissionId]: value === EXCLUDED_OPTION ? EXCLUDED : value },
                              );
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={UNSET_OPTION}>{t('Canvas.summary.distribute.choose')}</SelectItem>
                              <SelectItem value={EXCLUDED_OPTION}>{t('Canvas.summary.distribute.exclude')}</SelectItem>
                              {rosterIDs.map(id => (
                                <SelectItem key={id} value={id}>
                                  {studentNames[id] ?? id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {unresolvedCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('Canvas.summary.distribute.unresolved', { count: unresolvedCount })}
                </p>
              )}

              <DialogFooter>
                {lastReleasedAt && (
                  <Button
                    variant="ghost"
                    onClick={() => void handleRevoke()}
                    disabled={isReleasing}
                    className="mr-auto"
                  >
                    <Undo2 className="h-4 w-4 mr-1" /> {t('Canvas.summary.distribute.revoke')}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isReleasing}>
                  {t('Canvas.summary.distribute.cancel')}
                </Button>
                <Button onClick={() => void handleRelease()} disabled={!canRelease}>
                  <Check className="h-4 w-4 mr-1" /> {t('Canvas.summary.distribute.confirm')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
